import React, { useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';

import { Container } from '@mui/material';

import FindSimilarAccountsContent from '~/components/System/FindSimilarAccounts/FindSimilarAccountsContent';
import useSimilarAccounts from '~/components/System/FindSimilarAccounts/useSimilarAccounts';

import useCurrentTenant from '~/hooks/useCurrentTenant';

type Props = {
  user: App.User;
};

export default function SimilarAccountsTab({ user }: Props) {
  const { tenant } = useCurrentTenant();
  const [userPhone, setUserPhone] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  useEffect(() => {
    if (user.phone) {
      setUserPhone(user.phone);
    }
    if (user.email) {
      setUserEmail(user.email);
    }
  }, [user]);

  const {
    data: phoneData,
    error: phoneError,
    isFetching: phoneIsFetching,
    isLoading: phoneIsLoading,
  } = useSimilarAccounts({
    searchType: 'phone',
    searchValue: userPhone,
    brand: tenant.brand,
    enabled: Boolean(userPhone || userEmail),
  });

  const {
    data: emailData,
    error: emailError,
    isFetching: emailIsFetching,
    isLoading: emailIsLoading,
  } = useSimilarAccounts({
    searchType: 'email',
    searchValue: userEmail,
    brand: tenant.brand,
    enabled: Boolean(userEmail),
  });

  const error = phoneError || emailError;
  const isBusy = phoneIsFetching || phoneIsLoading || emailIsFetching || emailIsLoading;

  const results = useMemo(() => {
    const all = [
      ...(phoneData?.seedAccounts ?? []),
      ...(emailData?.seedAccounts ?? []),
      ...(phoneData?.similarAccounts ?? []),
      ...(emailData?.similarAccounts ?? []),
    ];
    const seenEmails = new Set<string>();
    return all.reduce((acc: typeof all, account) => {
      // Filter out the account with userEmail
      if (account.email && account.email === userEmail) {
        return acc;
      }
      if (!account.email) {
        acc.push(account);
        return acc;
      }
      if (seenEmails.has(account.email)) {
        return acc;
      }
      seenEmails.add(account.email);
      acc.push(account);
      return acc;
    }, []);
  }, [
    phoneData?.seedAccounts,
    emailData?.seedAccounts,
    phoneData?.similarAccounts,
    emailData?.similarAccounts,
    userEmail,
  ]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Users | Similar Accounts</title>
      </Helmet>

      <FindSimilarAccountsContent
        data={{
          seedAccounts: results ?? [],
          similarAccounts: [],
        }}
        error={error}
        isBusy={isBusy}
      />
    </Container>
  );
}
